import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MinimapCropPan, ReactIcons } from "@kalyzee/kalyzee-ui";
import { ContentLocationOutputDTO, VOD_STATE, VodOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { FullscreenContainer, FullscreenContainerRef, Loading, MultiAVPlayer, MultiAVPlayerDebugState, MultiAVPlayerEnginesConfig, MultiAVPlayerEventListenerMap, MultiAVPlayerRef, MultiAVPlayerDateDisplayMode, MultiAVPlayerRenderingContext, PlayerRenderItem, VideoNotAllowedStrategy, VideoSourceDated } from "@kalyzee/kast-web-sdk/web-components";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { logger } from "../../../../helpers/logger";
import { useSettings } from "../../../../hooks/settings";
import { useGridConfig } from "../../../../hooks/gridConfig";
import { MultiVideoPlayerDebugPane } from "../../../../components/debug/MultiVideoPlayerDebugPane";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { NewTriLayoutContainer, NewTriLayoutContainerProps } from "../../../../components/player/container/NewTriLayoutContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import { PlayerActionItem, PlayerWorkBox, PlayerWorkBoxEventLogEntry, PlayerWorkBoxNotice, PlayerWorkBoxOverlayContent } from "../../../../components/workBoxes/PlayerWorkBox";
import { LayoutSelector } from "../../../../components/common/LayoutSelector";
import { LayoutType } from "../../../../interfaces/ui";
import { useInteractiveVodAspectRatios } from "../../../../hooks/videoAspectRatio";
import { toastInfo, toastWarning } from "../../../../helpers/toast";
import { isAppleDevice, isAppleTouchDevice } from "../../../../helpers/platform";
import { getNormalUser } from "../../../../helpers/user";
import { TopBarSlot } from "../../../../components/common/TopBarSlot";
import { InteractionIsolate } from "../../../../components/utils/InteractionIsolate";
import { deriveTileNotice, FileAnnotations, mediaEventLabel } from "./replayMediaEvents";

// The hls.worker.js matching the hls.js the Video component runs; alias resolved in
// vite.config.ts.
import vodHlsWorkerUrl from "~vod-hls-worker?url";

import "./player.css";
import styles from "./player.module.css";
import clsx from "clsx";
import { getResolvedSettings, SettingsVod, VodWebCodecsMode } from "../../../../helpers/settings";

const VOD_LAYOUTS = [LayoutType.Full, LayoutType.Top, LayoutType.Right] as const;

/** Padding around the players (and the strip), by form factor. */
const PLAYERS_PADDING = 16;
const PLAYERS_PADDING_MOBILE = 6;

/**
 * Browser fullscreen on iPhone/iPad draws Safari's own close button over the player, next to
 * ours. The CSS fullscreen (fixed, viewport-sized) has no such chrome; the app's own exit
 * button is the only one.
 */
const useNativeFullscreen = !isAppleTouchDevice();

/**
 * Engines MultiAVPlayer may pick for the replay, from the settings and the platform.
 *
 * WebCodecs defaults to Apple devices only (Mac, iPhone, iPad). iOS/iPadOS is where the
 * media-element engine plays at WebKit's discretion (ManagedMediaSource arbitration,
 * appendBuffer deferred for minutes) and the reason the engine exists; the Mac shares the
 * hardware and gets the same engine. Elsewhere hls.js on MSE does the job and WebCodecs
 * hardware decoding has failure modes of its own (VAAPI on Linux/Intel). `'true'` lifts the
 * platform gate (A/B on other desktops), `'false'` disables the engine everywhere.
 *
 * Platform, not capability: the iPad has an unmanaged MediaSource, so a `window.MediaSource`
 * test would exclude the device this rule is written for.
 *
 * A config left with no engine gets the media-element engine back, the only one that runs
 * everywhere. Left alone, the SDK reads "no engine" as "both", which would bring WebCodecs
 * back where this rule just removed it.
 */
const resolveVodEngines = (engines: SettingsVod["engines"]): MultiAVPlayerEnginesConfig => {
  // Read as unknown: configs saved before the third mode existed hold booleans.
  const raw = engines?.webcodecs as unknown;
  const mode: VodWebCodecsMode =
    raw === true || raw === "true" ? "true" : raw === false || raw === "false" ? "false" : "apple";
  const webcodecs = mode === "true" || (mode === "apple" && isAppleDevice());
  const mediaElement = engines?.mediaElement !== false || !webcodecs;
  return { webcodecs, mediaElement };
};

const isHls = (c?: ContentLocationOutputDTO) => c?.videoType === "hls";

/** Storage no longer holds the file this record points at. */
const HTTP_GONE = 410;

/**
 * Status of an axios outcome, whether it arrived as a response or was thrown. The
 * client's `validateStatus` decides which, so both shapes are read.
 */
const statusOf = (outcomeOrError: any): number | undefined =>
  outcomeOrError?.status ?? outcomeOrError?.response?.status;

/** A content location plus what this page resolves before playback; no field is in the SDK DTO. */
type ResolvedContentLocation = ContentLocationOutputDTO & {
  /** Blob URL of the byte-range HLS playlist. Minted and revoked by this page. */
  byteRangeHlsUrl?: string;
  /** The file is not in storage, so the tile is dropped rather than pointed at missing bytes. */
  gone?: boolean;
  /** The recorded file's own annotations, as the resolve relayed them from inside the MP4. */
  annotations?: FileAnnotations;
};

/**
 * hls.js settings for VOD replay; the library's defaults target live streaming.
 *
 * `lowLatencyMode` shrinks the very buffer this path exists to obtain.
 * `maxBufferLength` can be deep here because hls.js fetches closed byte ranges, so
 * buffer depth costs no held-open response.
 * `backBufferLength` caps retention behind the playhead — a grid runs one hls.js
 * instance per camera and their memory adds up.
 *
 * The load policies allow ONE retry after 2s. Recovery belongs to the layers above
 * (Video's paced startLoad, then the tile retry backoff); hls.js's own retry bursts
 * stack with those and multiply into request storms against a failing URL.
 *
 * `preferManagedMediaSource: false` keeps hls.js on the unmanaged MediaSource wherever
 * one exists. Under ManagedMediaSource the browser owns the right to fetch and revokes
 * it without raising anything, which on a grid of always-mounted players is
 * indistinguishable from a tile that never loads. Where no unmanaged MediaSource
 * exists hls.js uses MMS regardless, and `prefersNativeHls` covers those platforms.
 *
 * `workerPath` points hls.js at the dist worker file instead of its inline worker,
 * which does not survive the production build and leaves every tile transmuxing on the
 * main thread. The file behind the alias is version-matched by vite.config.ts.
 */
const VOD_LOAD_POLICY = {
  default: {
    maxTimeToFirstByteMs: 10_000,
    maxLoadTimeMs: 60_000,
    timeoutRetry: { maxNumRetry: 1, retryDelayMs: 2_000, maxRetryDelayMs: 2_000 },
    errorRetry: { maxNumRetry: 1, retryDelayMs: 2_000, maxRetryDelayMs: 2_000 },
  },
};
const VOD_HLS_CONFIG = {
  lowLatencyMode: false,
  maxBufferLength: 60,
  backBufferLength: 30,
  preferManagedMediaSource: false,
  workerPath: vodHlsWorkerUrl,
  manifestLoadPolicy: VOD_LOAD_POLICY,
  playlistLoadPolicy: VOD_LOAD_POLICY,
  fragLoadPolicy: VOD_LOAD_POLICY,
};

/**
 * Whether to hand an HLS manifest straight to the <video> element instead of driving it
 * with hls.js. True only where the browser has no unmanaged MediaSource at all — iOS and
 * iPadOS, where every engine is WebKit and buffering stays at the browser's discretion.
 * WebKit's own player has no such arbitration.
 *
 * Tested with `canPlayType` rather than by platform, so a browser with no native HLS keeps
 * the hls.js path.
 *
 * The grid survives the swap because it synchronizes on plain HTMLMediaElement state —
 * currentTime, buffered, readyState, playbackRate — and never reads hls.js. Byte-range
 * manifests are standard HLS, so one URL serves both paths.
 */
let nativeHlsPreferred: boolean | undefined;
const prefersNativeHls = (): boolean => {
  if (nativeHlsPreferred === undefined) {
    nativeHlsPreferred =
      typeof window !== "undefined" &&
      !window.MediaSource &&
      !!document.createElement("video").canPlayType("application/vnd.apple.mpegurl");
    // Once per session, and only on the platforms that take this path: which backend
    // played a tile is the first thing to establish when a replay is reported stuck.
    if (nativeHlsPreferred) { 
      if (getResolvedSettings().dev) toastInfo("Playing HLS through native player");
      logger.log("[vod] no unmanaged MediaSource: playing HLS through the native player"); 
    }
  }
  return nativeHlsPreferred;
};

/**
 * Asks the published address for its JSON answer rather than its redirect. The route
 * content-negotiates and answers a redirect to anything that has not asked otherwise,
 * which is the right default for a media element and the wrong one here: following it
 * pulls the whole recording into memory and hides the case where the file is gone.
 *
 * Appended as text so the address is passed through exactly as published, absolute or
 * relative. Parsing it would need a base, and the only base available here is the page
 * origin — which is not the API's, so a relative address would be sent to the wrong host.
 */
const withJsonFormat = (videoUrl: string): string =>
  `${videoUrl}${videoUrl.includes("?") ? "&" : "?"}format=json`;

// The location the player shows for a VOD: HLS preferred, and only locations that
// carry a URL are candidates — a VOD can also expose url-less file/hls locations, and
// one of those would win the ranking and drop the VOD from the grid.
const pickContentLocation = (v: VodOutputDTO): ContentLocationOutputDTO | undefined => {
  const getPriority = (c: ContentLocationOutputDTO) => (isHls(c) ? 1 : 0);
  const candidates = (v.contentLocations ?? []).filter((c) => !!c.videoUrl);
  if (!candidates.length) return undefined;
  // Sorted on a copy: `sort` mutates, and `v.contentLocations` belongs to the caller.
  return [...candidates].sort((a, b) => getPriority(b) - getPriority(a))[0];
};

/**
 * How often the per-tile notices are re-derived from the playhead. The playhead
 * itself is reported 20×/s while playing; deriving (and re-rendering) at that rate
 * would cost the whole grid a render per tick for a caption that changes a handful of
 * times per session.
 */
const TILE_NOTICE_REFRESH_MS = 250;

/**
 * How far the playhead may have advanced since the previous notice pass for that pass to
 * be allowed to announce anything. A few ticks' worth: playback moves by about one
 * refresh per pass, a scrub moves by minutes, and only the first is a transition someone
 * was watching happen.
 */
const TILE_TOAST_MAX_ADVANCE_MS = TILE_NOTICE_REFRESH_MS * 4;

/**
 * Identity of a replay tile, and the only one the layout ever sees. The engines'
 * own `PlayerRenderItem.key` cannot play that role: it differs between the two of
 * them and, in the WebCodecs engine, changes with the tile's decode mode — while
 * this key is what keeps a tile's grid slot and the focus across mounts. Derived
 * from the recording instead, so it holds whether or not the engine has the source
 * mounted right now.
 *
 * Kept although the grid now paints only what the engine hands back: what it fixes is
 * the engines' own key churn, which has nothing to do with which tiles exist.
 */
const tileKey = (source: VideoSourceDated, index: number) => `vod_${source.id ?? index}`;

/** A tile's notice, formatted for PlayerWorkBox: dates already turned into text. */
interface DisplayTileNotice {
  overlay?: PlayerWorkBoxNotice;
  /** Drawn as the tile's audio state — the same muted mic a live participant gets. */
  audioMuted: boolean;
}

/** Who a recorded tile shows, when the VOD names them: the full-cover notice puts a face on it. */
interface TileUser {
  userId?: string;
  displayName?: string;
  avatar?: string;
}

/**
 * An instant on a tile's timeline, as an offset from the start of its own recording —
 * which is how the file numbers its events, and the only form comparable between two
 * tiles of the grid. Never negative: an event before the file's first frame does not
 * exist in the annotations.
 */
const formatTileOffset = (ms: number): string => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
};

export interface VodPlayerContentPageProps {
  /** The meeting's playable VODs (provided by the accessibility endpoint). */
  vods: VodOutputDTO[];
  /** Header title. */
  meetingName?: string;
  /** Shown next to the title, when known. */
  expertName?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const VodPlayerContentPage = ({ vods: vodsInput, meetingName, expertName, className, style }: VodPlayerContentPageProps) => {
  const [loading, setLoading] = useState(true);
  const [vods, setVods] = useState<VodOutputDTO[]>([]);
  const [debugState, setDebugState] = useState<MultiAVPlayerDebugState | null>(null);
  const playerRef = useRef<MultiAVPlayerRef>();
  const sdkCtx = useSdkContext();
  const [settings] = useSettings();
  const gridConfig = useGridConfig();

  // How each tile will be played, decided once before any media byte is fetched. One
  // request per tile: `videoUrl` answers with the signed URL and, when the recording has
  // one, the byte-range HLS playlist inline.
  //
  // HLS is preferred wherever it exists — it is the only path where buffer depth is
  // independent of how long a response is held open. Whether a recording has one is a
  // property of its bytes, so it is reported rather than assumed.
  //
  // The signed URL is worth taking either way: it skips a redirect on every seek past the
  // buffer, and it outlives the session where the `/cl` link's ts/sig expires.
  //
  // Failure leaves the `/cl` URL in place and the tile still plays — except on 410, where
  // there is nothing at the end of any URL for that recording.
  const resolvePlayableUrls = async (vods: VodOutputDTO[]) => {
    const client = sdkCtx.client.getHttpClient().client;
    await Promise.all(
      vods.map(async (v) => {
        const cl = pickContentLocation(v) as ResolvedContentLocation | undefined;
        if (!cl || isHls(cl) || !cl.videoUrl) return;

        try {
          // Fetched at the published address, with the query parameter that asks for
          // the answer instead of the redirect. The route also honours an
          // `Accept: application/json` header, but the header a request carries is
          // decided by the HTTP client rather than by this page, so the parameter is
          // what makes the choice explicit here.
          const r = await client.get(withJsonFormat(cl.videoUrl));
          if (statusOf(r) === HTTP_GONE) {
            cl.gone = true;
            return;
          }
          if (r.status !== 200) return;
          if (r.data?.url) cl.videoUrl = r.data.url;

          // The file's own annotations, when it carries any. Tested on the envelope's
          // `kind` rather than on its presence: the field is relayed verbatim from inside
          // the MP4, so a payload that does not announce itself is not one this page can
          // read. Which entry of the envelope says what is `deriveTileNotice`'s business.
          if (r.data?.annotations
            && typeof r.data.annotations === "object"
            && r.data.annotations.kind === "kalyzee.annotations") {
            cl.annotations = r.data.annotations as FileAnnotations;
          }

          // hls.js needs a URL and the playlist arrived as text. Every segment line
          // carries an absolute signed URL, so nothing resolves against the blob's origin.
          if (r.data?.format === "hls" && typeof r.data?.manifest === "string") {
            const blob = URL.createObjectURL(
              new Blob([r.data.manifest], { type: "application/vnd.apple.mpegurl" }),
            );
            blobUrls.current.push(blob);
            cl.byteRangeHlsUrl = blob;
          }
        } catch (err) {
          // 410 must not fall back: the `/cl` URL would resolve to a file that is not
          // in storage, failing at the object store instead of here.
          if (statusOf(err) === HTTP_GONE) {
            cl.gone = true;
            return;
          }
          // keep the /cl URL — tile still plays via the redirect path
        }
      }),
    );
  };

  // A blob pins its playlist in memory until revoked, and these run to hundreds of KB
  // each. The list is re-resolved whenever it changes, so every one minted is tracked.
  const blobUrls = useRef<string[]>([]);

  const releaseBlobUrls = () => {
    blobUrls.current.forEach((u) => URL.revokeObjectURL(u));
    blobUrls.current = [];
  };

  // The player receives its VODs from the caller (the accessibility endpoint),
  // it never fetches them itself — that endpoint is public, so playback works
  // logged-out. We only resolve their playable URLs once (best-effort), on a
  // copy of the content locations so the signed-URL swap doesn't mutate the
  // caller's accessibility data.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Backend is filtering away non terminated VODs on the /meeting/accessibility route already
    // filtering here again just in case
    const copy = vodsInput.filter(v => v.state === VOD_STATE.TERMINATED).map((v) => ({
      ...v,
      contentLocations: v.contentLocations?.map((cl) => ({ ...cl })),
    }));
    (async () => {
      await resolvePlayableUrls(copy);
      if (cancelled) {
        // Superseded while resolving: the cleanup already ran, so nothing else would
        // free the blobs minted since.
        releaseBlobUrls();
        return;
      }
      setVods(copy);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
      releaseBlobUrls();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vodsInput]);

  useEffect(() => {
    if (!settings.dev) return;
    const id = setInterval(() => {
      const state = playerRef.current?.getDebugState();
      if (state) setDebugState(state);
    }, 500);
    return () => clearInterval(id);
  }, [settings.dev]);

  // Dev mode: surface which playback engine the wrapper picked in the browser tab. Reads the
  // ref after the player mounted (hence the `loading` dep), restores the title on leave.
  useEffect(() => {
    if (!settings.dev || loading) return;
    const engine = playerRef.current?.getEngine();
    if (!engine) return;
    const previousTitle = document.title;
    document.title = `[${engine === "webcodecs" ? "webCodecs" : "mediaElements"}] ${previousTitle}`;
    return () => {
      document.title = previousTitle;
    };
  }, [settings.dev, loading]);

  const data = useMemo<VideoSourceDated[]>(() => {
    const result: VideoSourceDated[] = [];
    
    // Client-clock alignment. The retransmission URLs can carry the wallclock at which
    // each publish attempt started ({{attemptAt}} placeholder), persisted by the recording
    // chain as extra.clientPublishStartedAt (epoch ms). Those timestamps all come from the
    // ONE page that published every stream of the meeting — a single clock — so their
    // relative spacing is reliable, unlike recordStartedAt whose per-file stamping delay
    // is precisely what desyncs the replays (audio mix vs cameras).
    const clientPublishStartedAt = (v: VodOutputDTO): number | undefined => {
      const t = Number((v.extra as any)?.clientPublishStartedAt);
      return Number.isFinite(t) && t > 0 ? t : undefined;
    };
    const playable = vods.filter((v) => (pickContentLocation(v) as ResolvedContentLocation | undefined)?.videoUrl);

    // All-or-nothing: placing some files on the client clock and the rest on
    // recordStartedAt would mix two referentials whose origins differ by an unknown
    // delay — the very offset this corrects. One untagged file → DB dates for all.
    const useClientClock = playable.length > 0 && playable.every((v) => clientPublishStartedAt(v) !== undefined);

    playable.forEach((v) => {
      const contentLocation = pickContentLocation(v) as ResolvedContentLocation | undefined;
      const src = contentLocation?.videoUrl;
      // Byte-range HLS over one fragmented MP4 (confirmed available in resolvePlayableUrls),
      // or the legacy segmented-HLS content type.
      const hlsSrc = contentLocation?.byteRangeHlsUrl ?? (isHls(contentLocation) ? src : undefined);
      // Same manifest either way; only the engine differs. A plain string goes to the element
      // (WebKit's native player), a {type:'hls'} object goes to hls.js — see prefersNativeHls.
      const nativeHls = !!hlsSrc && prefersNativeHls();
      const device = (v as any).device;
      const name = device?.name ?? (v as any).user?.identity?.username ?? new Date(v.recordStartedAt).toLocaleString();

      // A recording whose file is not in storage has no playable form, so it drops out
      // of the grid like a location carrying no URL: one fewer camera, not a dead tile.
      if (!contentLocation || contentLocation.gone || !src) return;
      const startedAtMs = useClientClock ? clientPublishStartedAt(v)! : new Date(v.recordStartedAt).getTime();

      result.push({
        id: v.id,
        src: hlsSrc ? (nativeHls ? hlsSrc : { type: "hls", src: hlsSrc }) : src,
        name,
        startedAt: new Date(startedAtMs),
        // The client clock repositions a file on the shared timeline; its length still
        // comes from the record dates.
        endedAt: new Date(startedAtMs + (new Date(v.recordEndedAt).getTime() - new Date(v.recordStartedAt).getTime())),
        // Per-source, so a grid mixing fragmented and progressive recordings tunes only
        // the tiles that actually run hls.js — a natively played manifest has no hls.js to tune.
        ...(hlsSrc && !nativeHls ? { nativeProps: { hlsConfig: VOD_HLS_CONFIG } } : {}),
      });
    });

    return result;
  }, [vods]);

  // The person behind each recording, for the full-cover notices: a switched-off camera
  // shows their avatar in a live meeting, so the replay of that same moment does too.
  // Same source as the tile's name above (`user.identity`); a VOD without a user simply
  // falls back to the notice's icon.
  const tileUsers = useMemo(() => {
    const result = new Map<string, TileUser>();
    vods.forEach((v) => {
      const user = (v as any).user;
      if (!user || typeof user !== "object") return;
      const { displayName, avatar } = getNormalUser(user);
      const userId = user.id ?? user._id;
      result.set(v.id, {
        userId: userId ? String(userId) : undefined,
        displayName: displayName || undefined,
        avatar: avatar || undefined,
      });
    });
    return result;
  }, [vods]);

  // Vods flagged `extra.audioOnly` carry a black video track (an OvenMedia recording
  // constraint) — never show their tile nor let it claim a pagination slot. They MUST
  // stay inside the multiplayer though (not be mounted apart): it owns the master
  // clock, drift resync, stall handling and the timeline segments, so keeping the
  // source and hiding its PlayerItem gives synced audio for free (hidden items stay
  // mounted, they're just excluded from the layout). Declared here rather than beside
  // renderPlayersLayout because the notice pass below reads the same set.
  const audioOnlyVodIds = useMemo(
    () => new Set(vods.filter((v) => v.extra?.audioOnly).map((v) => v.id)),
    [vods],
  );

  /* --------------------------- Per-tile notices ---------------------------- */
  // Why a tile is black: the recorded media's own history, when the file carried it.
  //
  // The playhead lives in a ref, not in state: `currentTime` fires every 50 ms while
  // playing and a setState per tick would re-render the whole grid. It is also
  // reported on every seek — both engines run the same update pass from `seekTo` —
  // so the notices follow a scrub while paused, without polling the engine.
  const playheadRef = useRef<Date | undefined>(undefined);
  const [tileNotices, setTileNotices] = useState<Record<string, DisplayTileNotice>>({});
  // What each tile is currently saying. Notices are pushed to state only when this
  // changes, so a whole session of playback costs a handful of renders.
  const tileNoticeSignaturesRef = useRef<Record<string, string>>({});
  // Same history, chronological, drawn over each tile in dev — built on the same pass
  // and under the same rule: pushed to state only when a line crosses the playhead.
  const [tileEvents, setTileEvents] = useState<Record<string, PlayerWorkBoxEventLogEntry[]>>({});
  const tileEventSignaturesRef = useRef<Record<string, string>>({});
  // What the same pass last saw of the two facts worth announcing out loud: whether the
  // tile was in the mosaic, and whether its picture was masked at the source. Camera and
  // mic toggles are deliberately not among them — frequent and unremarkable in a class,
  // and the overlay and the mic badge already carry them on the tile itself.
  const tileToastStatesRef = useRef<Record<string, { onGrid: boolean; hidden: boolean }>>({});
  // The playhead the previous pass ran on, which is what tells a tick of playback from a
  // seek. `undefined` until a pass has run.
  const toastPlayheadRef = useRef<number | undefined>(undefined);

  const playerCallbacks = useMemo<Partial<MultiAVPlayerEventListenerMap>>(() => ({
    currentTime: (_time: number, date: Date) => { playheadRef.current = date; },
    // Fires once per data change, with the grid's first instant — a playhead before
    // the first tick, so a tile whose file opens on a cut camera is captioned from
    // its very first frame rather than from the first `currentTime`.
    duration: (_duration: number, start: Date) => { if (!playheadRef.current) playheadRef.current = start; },
  }), []);

  useEffect(() => {
    const vodsById = new Map(vods.map((v) => [v.id, v]));
    // Where the player sits before it has reported anything: its first instant, the
    // same one `duration` announces. Without it the first derivation claims nothing
    // and a file's opening state goes uncaptioned until the next pass.
    const gridStart = data.reduce<Date | undefined>(
      (earliest, source) => (!earliest || source.startedAt < earliest ? source.startedAt : earliest),
      undefined,
    );
    // The pass derives captions from a payload relayed verbatim out of a file, and it runs
    // four times a second: a throw in one tile's derivation must never take the replay page
    // with it. On failure nothing is pushed, so the tiles keep the notices they already
    // show — a caption one pass stale beats a blank page. Warned once per pass instance,
    // and only in dev: repeating it 4×/s would say nothing more.
    let warned = false;
    const compute = () => {
      try {
        const playhead = playheadRef.current ?? gridStart;

        const previousSignatures = tileNoticeSignaturesRef.current;
        const signatures: Record<string, string> = {};
        const next: Record<string, DisplayTileNotice> = {};
        let changed = false;

        // The chronological log is a dev affair, so it is only built in dev: one row per
        // transition per tile is by far the most expensive thing this 4×/s pass could do,
        // and nothing reads it otherwise. Leaving the map empty also clears the tiles when
        // dev mode is switched off mid-replay.
        const nextEvents: Record<string, PlayerWorkBoxEventLogEntry[]> = {};
        const previousEventSignatures = tileEventSignaturesRef.current;
        const eventSignatures: Record<string, string> = {};
        let eventsChanged = false;

        // A transition is only worth announcing when the viewer was there to miss it. The
        // pass runs 4×/s over every tile and the playhead also moves on every scrub, so
        // without this gate a single drag across the timeline would fire every transition
        // of the session at once. Only a playhead that moved forward by about one pass
        // toasts; a seek, a rewind and the very first pass merely record where each tile
        // now stands, silently, so the next real transition is still detected against the
        // right state and nothing has piled up behind it.
        const playheadMs = playhead ? playhead.getTime() : undefined;
        const previousPlayheadMs = toastPlayheadRef.current;
        const advance = playheadMs !== undefined && previousPlayheadMs !== undefined
          ? playheadMs - previousPlayheadMs
          : undefined;
        const mayToast = advance !== undefined && advance > 0 && advance <= TILE_TOAST_MAX_ADVANCE_MS;
        const previousToastStates = tileToastStatesRef.current;
        const toastStates: Record<string, { onGrid: boolean; hidden: boolean }> = {};

        data.forEach((source) => {
          const id = String(source.id ?? "");
          const vod = id ? vodsById.get(id) : undefined;
          if (!vod) return;
          // The annotations travel with the location the tile actually plays — the same
          // pick `data` made — because they were read from that file's own bytes.
          const contentLocation = pickContentLocation(vod) as ResolvedContentLocation | undefined;
          const notice = deriveTileNotice({ playhead, source, annotations: contentLocation?.annotations });
          // The current state only: what covers the picture, never since when. The
          // instant stays in the dev pane's log for whoever needs it.
          const overlay: PlayerWorkBoxNotice | undefined = notice.overlay && {
            kind: notice.overlay.kind,
            label: notice.overlay.label,
          };
          signatures[id] = [overlay?.kind ?? "", overlay?.label ?? "", notice.audioMuted ? "muted" : ""].join("|");
          if (signatures[id] !== previousSignatures[id]) changed = true;
          next[id] = { overlay, audioMuted: notice.audioMuted };

          // Whether the tile is part of the mosaic right now, read from its own span
          // rather than from the render: the engines drop a source from `players`
          // outside it (see renderPlayersLayout), so the grid reflows with no
          // explanation — but a render callback is the wrong place to fire anything.
          const onGrid = playheadMs !== undefined
            && playheadMs >= source.startedAt.getTime()
            && playheadMs <= source.endedAt.getTime();
          // The masked picture as the viewer gets it: what the overlay ended up saying,
          // so a tile whose media is gone announces nothing about its picture.
          const hidden = overlay?.kind === "display_hidden";
          const previousToastState = previousToastStates[id];
          toastStates[id] = { onGrid, hidden };
          // The full-sound mix is in `data` — it has to be, that is how it stays synced —
          // but it is never drawn (see `audioOnlyVodIds`), so its span opening or closing
          // changes nothing on screen and announcing it would describe a tile the viewer
          // cannot see. Only the toast is skipped: the derivation above still runs, and the
          // states are still recorded, so nothing downstream knows the difference.
          if (mayToast && previousToastState && !audioOnlyVodIds.has(id)) {
            // Built in `data` as device name, then username, then the record date — never
            // empty, so there is nothing to fall back to. An unnamed device does toast its
            // start date, the same string its tile is labelled with.
            const name = source.name;
            // Arrival and departure win over the picture: a tile leaving carries no
            // overlay any more, and announcing both of a tile that arrives already
            // masked would say the same thing twice.
            //
            // « le cours » rather than « la mosaïque »: these replays are classes, and
            // the app already calls the lesson that — « Lancer le cours », « Terminer le
            // cours ». It reads oddly for a fixed camera and perfectly for a person, and
            // the product has personified cameras as participants since the live grid.
            if (onGrid !== previousToastState.onGrid) {
              toastInfo(onGrid ? `${name} rejoint le cours` : `${name} quitte le cours`);
            } else if (hidden !== previousToastState.hidden) {
              // Read from the registry, never copied out of it: these are the same two
              // transitions the dev pane's log names, and one wording for one fact.
              const label = mediaEventLabel(hidden ? "display_hidden" : "display_shown");
              if (hidden) toastWarning(`${label} : ${name}`);
              else toastInfo(`${label} : ${name}`);
            }
          }

          if (!settings.dev) return;
          const startedAtMs = source.startedAt.getTime();
          const entries = notice.events.map((e) => ({
            time: formatTileOffset(e.at.getTime() - startedAtMs),
            label: e.label,
            past: e.past,
          }));
          nextEvents[id] = entries;
          // The `past` flags move with the playhead, so the count of past entries is part
          // of what the pane is saying.
          eventSignatures[id] = `${entries.length}|${entries.reduce((acc, e) => acc + (e.past ? 1 : 0), 0)}`;
          if (eventSignatures[id] !== previousEventSignatures[id]) eventsChanged = true;
        });

        // Recorded whether or not anything was announced — that is what keeps a seek
        // silent without losing the transition that follows it.
        tileToastStatesRef.current = toastStates;
        toastPlayheadRef.current = playheadMs;

        if (eventsChanged || Object.keys(eventSignatures).length !== Object.keys(previousEventSignatures).length) {
          tileEventSignaturesRef.current = eventSignatures;
          setTileEvents(nextEvents);
        }

        if (!changed && Object.keys(signatures).length === Object.keys(previousSignatures).length) return;
        tileNoticeSignaturesRef.current = signatures;
        setTileNotices(next);
      } catch (error) {
        if (settings.dev && !warned) {
          warned = true;
          console.warn("[VOD] tile notice pass failed, keeping the previous notices: ", error);
        }
      }
    };

    compute();
    const timer = setInterval(compute, TILE_NOTICE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [data, vods, audioOnlyVodIds, settings.dev]);

  const info = useMemo<React.ReactNode>(() => {
    if (!meetingName && !expertName) return undefined;
    return (
      <div className={styles.infoContainer}>
        <div className={styles.meetingName}>{meetingName}</div>
        <div className={styles.expertName}>{expertName}</div>
      </div>
    );
  }, [meetingName, expertName]);

  const layout = useAppStoreSelector((state) => state.layout);
  const isMobile = useAppStoreSelector((state) => state.isMobile);
  const [fullscreenItemId, setFullscreenItemId] = useState<string | undefined>();

  // Interactive (playerFit) support, mirroring the live dashboard: the flag
  // and the fallback dimensions come from the VOD's backend `extra` (frozen at
  // record time) instead of the live media's `extra`. Ratios are read from the
  // <video> elements (no MediaStream here); the pan is plain page state
  // (nothing to share).
  const playerRefsMapRef = useRef(new Map<string, React.RefObject<HTMLDivElement>>());
  const getPlayerRef = useCallback((id: string) => {
    let ref = playerRefsMapRef.current.get(id);
    if (!ref) {
      ref = { current: null };
      playerRefsMapRef.current.set(id, ref);
    }
    return ref;
  }, []);
  
  const interactiveRatios = useInteractiveVodAspectRatios(
    vods,
    useCallback((id: string) => playerRefsMapRef.current.get(id)?.current ?? null, []),
  );
  const [minimapPan, setMinimapPan] = useState<Record<string, MinimapCropPan | undefined>>({});

  const enableTimelineZoom = settings.ui?.enableTimelineZoom === true;

  // Native fullscreen control inside the timeline is disabled; we drive it from
  // each player's overlay action instead. On mobile the volume opens as a popup (the row is
  // too short for an inline slider) and the layout selector sits in the top bar.
  const controlsProps = useMemo(() => ({
    header: info,
    displayFullscreen: false,
    volumeMode: isMobile ? "popup" as const : "inline" as const,
    timeProgressProps: { zoomOnLongPress: enableTimelineZoom },
  }), [info, isMobile, enableTimelineZoom]);
  const watermarkProps = useMemo(() => ({ element: <ReactIcons.LogoFull />, bottom: 20, right: 30 }), []);

  const fullscreenContainerRef = useRef<FullscreenContainerRef>(null);

  // Whether the player currently shows its control bar. Written by renderPlayersLayout (which
  // receives it from the player) for the tile renderer below, called in the same render pass.
  const controlsShownRef = useRef(true);

  // Sync browser fullscreen with the controlled state. Pressing Esc fires
  // onFullScreen(false), so we clear the item when that happens.
  const onFullscreenChange = useCallback((enabled: boolean) => {
    if (!enabled) setFullscreenItemId(undefined);
  }, []);

  const triLayoutRenderItem = useCallback<NonNullable<NewTriLayoutContainerProps["renderItem"]>>(
    (rendererProvider, { item, isInStrip, isFullscreen, onFocus }) => {
      const data = item.getData();
      if (data.type !== "vod_player") return null;
      const label = data.player.source.name ?? "";
      const vodId = String(data.player.source.id ?? "");

      // Interactive sources get the crop + minimap (owned by PlayerWorkBox).
      // The minimap gets a placeholder: the VOD <video> is ref-controlled by
      // MultiAVPlayer, so mounting it a second time would steal the ref.
      const minimapRatio = interactiveRatios[vodId];
      const isInteractive = minimapRatio !== undefined;

      // Why this tile shows what it shows at the current playhead (see the notice
      // effect above). Absent until the first derivation, and for tiles whose VOD is
      // no longer in the list.
      const tileNotice = tileNotices[vodId];
      const tileUser = tileUsers.get(vodId);

      // Mobile has no hover to reveal a tile's overlay (topbar actions): the overlays show and
      // hide with the control bar instead, so the fullscreen toggle is there as soon as the bar
      // is, and one tap on the video hides everything. Strip tiles get no selection overlay
      // there: a tap anywhere on one makes it the main player, control bar shown or not (the
      // frame stays tappable while the bar is hidden, see .hideControls).
      const controlsShown = controlsShownRef.current;
      const overlayMode = isMobile ? (controlsShown ? "overlay" : "overlay-hoverable") : "overlay-hoverable";
      const overlayContent: PlayerWorkBoxOverlayContent = isInStrip && !isMobile ? {
        type: "selection",
        layout,
        onSelect: onFocus,
      } : undefined;
      const boxProps = isMobile && isInStrip ? { onClick: onFocus } : undefined;

      // Fullscreen toggle lives in the topbar.
      const topbarActions: PlayerActionItem[] = [];
      if (!isFullscreen) {
        topbarActions.push({
          key: "fullscreen",
          icon: <ReactIcons.Expand />,
          onClick: () => setFullscreenItemId(item.getId()),
          label: "Plein écran",
        });
      } else {
        topbarActions.push({
          key: "exit-fullscreen",
          icon: <ReactIcons.Shrink />,
          onClick: () => setFullscreenItemId(undefined),
          label: "Quitter le plein écran",
        });
      }
      return rendererProvider((children) => (
        <PlayerWorkBox
          label={label}
          userId={tileUser?.userId}
          userDisplayName={tileUser?.displayName}
          avatar={tileUser?.avatar}
          video
          // The file's own account of the mic, once derived; `undefined` until then and
          // for a file carrying no history — nothing to report is not the same as muted.
          audio={tileNotice ? !tileNotice.audioMuted : undefined}
          loading={data.player.stalling}
          broken={data.player.broken}
          notice={tileNotice?.overlay}
          eventLog={tileEvents[vodId]}
          isSmall={isInStrip}
          isSidePlayer={isInStrip}
          isFullscreen={isFullscreen}
          overlayMode={overlayMode}
          overlaysHidden={isMobile && !controlsShown}
          topbarActions={topbarActions}
          overlayContent={overlayContent}
          boxProps={boxProps}
          topBarClassName={styles.playerTopBar}
          minimap={{
            enabled: isInteractive,
            ratio: minimapRatio,
            // Explicit null (not undefined): the provider would fall back to the
            // player node and mount the ref-controlled <video> twice. The minimap
            // stays transparent — selection frame + outside dim are enough.
            minimapContent: null,
            pan: minimapPan[vodId],
            onPanChange: (p) => setMinimapPan((prev) => ({ ...prev, [vodId]: p })),
            // Top-anchored so the stack doesn't sit on the VOD timeline bar.
            position: "top",
          }}
          containerRef={getPlayerRef(vodId)}
        >
          {children}
        </PlayerWorkBox>
      ));
    },
    [layout, interactiveRatios, minimapPan, getPlayerRef, isMobile, tileNotices, tileEvents],
  );

  const renderPlayersLayout = useCallback(
    (players: PlayerRenderItem[], ctx: MultiAVPlayerRenderingContext) => {
      // One tile per source the engine hands back, and a tile only while its file
      // plays: both engines drop a source from `players` outside its own span, so a
      // recording that has not started or is over simply has no frame here. Audio-only
      // recordings keep theirs mounted but out of the layout — they carry a black video
      // track (an OvenMedia recording constraint), see `audioOnlyVodIds`.
      const items = players.map((player, index) => new PlayerItem({
        type: "vod_player",
        // The engine's key is replaced, never its node — see tileKey.
        player: { ...player, key: tileKey(player.source, index) },
        hidden: !player.display || audioOnlyVodIds.has(String(player.source.id ?? "")),
      }));
      controlsShownRef.current = ctx.displayControls;
      // Isolated: inside a player whose control bar toggles on touch, switching layout is not a
      // tap on the video (harmless up in the top bar).
      const layoutSelector = !fullscreenItemId && (
        <InteractionIsolate>
          <LayoutSelector
            className={styles.layoutSelector}
            orientation={isMobile ? "horizontal" : "vertical"}
            layouts={VOD_LAYOUTS}
            defaultLayout={LayoutType.Top}
          />
        </InteractionIsolate>
      );
      const padding = fullscreenItemId ? 0 : isMobile ? PLAYERS_PADDING_MOBILE : PLAYERS_PADDING;
      return (
        <div className={clsx(isMobile ? styles.playersLayoutMobile : styles.playersLayout, { [styles.hideControls]: !ctx.displayControls })}>
          {/* Mobile: the selector goes up to the top bar, the players get the whole area. */}
          {isMobile ? <TopBarSlot>{layoutSelector}</TopBarSlot> : layoutSelector}
          <div className={styles.playersLayoutContent}>
            <NewTriLayoutContainer
              items={items}
              layout={layout}
              // Off-page tiles keep playing (hidden) so nobody's audio drops when paginating.
              keepOffPageItemsMounted
              gridMode={gridConfig.gridMode}
              pageSize={gridConfig.pageSize}
              autoTileMinPx={gridConfig.autoTileMinPx}
              fullscreenItemId={fullscreenItemId}
              renderItem={triLayoutRenderItem}
              stripPadding={padding}
              style={{ padding, boxSizing: "border-box" }}
            />
          </div>
        </div>
      );
    },
    [isMobile, layout, fullscreenItemId, triLayoutRenderItem, gridConfig, audioOnlyVodIds],
  );

  if (loading) return <Loading fullscreen message="Chargement en cours..." style={style} />;

  const renderContent = () => {
    return (
      <FullscreenContainer
        ref={fullscreenContainerRef}
        useNative={useNativeFullscreen}
        fullscreen={!!fullscreenItemId}
        onFullScreen={onFullscreenChange}
        style={{ width: "100%", height: "100%" }}
      >
        <>
          <MultiAVPlayer
            ref={playerRef}
            className={styles.vods}
            // Same wiring as the SDK loggers in App.tsx: the shared timestamped logger,
            // gated by a per-domain settings flag (dev mode falls back to console.log);
            // undefined = the player stays silent. Single wiring on purpose: a second
            // `logger` prop further down (even from a spread) silently overrides this one —
            // that exact bug shipped once, don't reintroduce it.
            logger={settings.logs?.vodPlayer ? logger.log : settings.dev ? console.log : undefined}
            // Opt-in (settings → Replay (VOD)): one player init at a time, ack-chained. Default off.
            sequentialTileMounts={settings.vod?.sequentialTileMounts ?? false}
            // Engine policy (settings → Replay (VOD) → Moteurs de lecture), see resolveVodEngines.
            // The wrapper resolves once at mount and capability wins over the config.
            engines={resolveVodEngines(settings.vod?.engines)}
            // Decoder pool of the WebCodecs engine (same settings group), ignored by the media-element
            // engine. The SDK leaves it off; this app turns it on unless the setting disables it.
            webcodecsDecoderPoolEnabled={!settings.vod?.engines?.disableWebcodecsDecoderPool}
            controls={controlsProps}
            //watermark={watermarkProps}
            dateDisplayMode={MultiAVPlayerDateDisplayMode.DATE_TIME}
            data={data}
            // Wall-clock playhead + grid span, feeding the per-tile notices. Stable
            // identity: the player keeps it in a ref, so a new object every render
            // would churn it for nothing.
            callbacks={playerCallbacks}
            renderPlayersLayout={renderPlayersLayout}
            // Engine diagnostic logger, dev mode only. Spread + cast because the installed SDK
            // typings may predate the `logger` prop; the engine ignores unknown props.
            {...({ logger: settings.dev ? console.log : undefined } as Record<string, unknown>)}
            muted={false}
            notAllowedStrategy={VideoNotAllowedStrategy.PLAY_WITH_MUTE}
          />
          <MultiVideoPlayerDebugPane state={debugState} className={styles.debugOverlay} />
        </>
      </FullscreenContainer>
    );
  };
  const classes = [styles.container];
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")} style={style}>
      {renderContent()}
    </div>
  );
};

